import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AtSign, CalendarClock, Gift, KeyRound, Loader2, LogOut, Mail, MapPin, Package, Receipt, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FREQUENCY_OPTIONS } from "@/components/NewsletterForm";
import PasswordInput from "@/components/PasswordInput";
import ReassignSchoolModal from "@/components/ReassignSchoolModal";
import MyChildren from "@/components/MyChildren";
import ChildThanks from "@/components/ChildThanks";
import ReferralShare from "@/components/ReferralShare";
import SchoolOrderLink from "@/components/SchoolOrderLink";
import PendingOrderActions from "@/components/PendingOrderActions";

const inputClass = "w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep";

const PAYMENT_LABELS = { paid: "Payée", pending: "En attente", failed: "Échouée", expired: "Expirée", refunded: "Remboursée", awaiting_cheque: "Chèque attendu", cancelled: "Annulée" };
const PAYMENT_STYLES = { paid: "bg-sage-light text-sage", pending: "bg-amber-100 text-amber-800", failed: "bg-terra/15 text-terra", expired: "bg-sand text-bark-soft", refunded: "bg-terra/15 text-terra" };
const FULFILLMENT_LABELS = { preparation: "En préparation", expediee: "Expédiée", livree: "Livrée" };

export default function CustomerAccountPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("coordonnees");
  const [profile, setProfile] = useState({ name: "", phone: "", address_line: "", postal_code: "", city: "" });
  const [saving, setSaving] = useState(false);
  const [orders, setOrders] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  const [emailForm, setEmailForm] = useState({ new_email: "", password: "" });
  const [savingEmail, setSavingEmail] = useState(false);
  const [newsletter, setNewsletter] = useState(null);
  const [nlFrequency, setNlFrequency] = useState("monthly");
  const [savingNl, setSavingNl] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPassword, setSavingPassword] = useState(false);
  const [reassignOrder, setReassignOrder] = useState(null);
  const [schoolChoices, setSchoolChoices] = useState({ schools: [], current_school_id: null });

  const openReassign = async (o) => {
    try {
      const { data } = await api.get("/customer/schools");
      setSchoolChoices(data);
      setReassignOrder(o);
    } catch { toast.error("Liste des écoles indisponible"); }
  };

  const submitReassign = async (f) => {
    try {
      const { data } = await api.post(`/customer/orders/${reassignOrder.session_id}/reassign-school`, f);
      toast.success(data.message);
      setReassignOrder(null);
      const { data: refreshed } = await api.get("/customer/orders");
      setOrders(refreshed);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const loadLoyalty = () => {
    api.get("/customer/loyalty").then(({ data }) => setLoyalty(data)).catch(() => setLoyalty({ points: 0, movements: [] }));
    api.get("/loyalty/settings").then(({ data }) => setLoyaltySettings(data)).catch(() => {});
  };

  const loadNewsletter = () => {
    api.get("/customer/newsletter").then(({ data }) => {
      setNewsletter(data);
      setNlFrequency(data.frequency || "monthly");
    }).catch(() => setNewsletter({ subscribed: false, frequency: "monthly" }));
  };

  useEffect(() => {
    if (user && user !== false) {
      setProfile({
        name: user.name || "",
        phone: user.phone || "",
        address_line: user.address_line || "",
        postal_code: user.postal_code || "",
        city: user.city || "",
      });
      setEmailForm({ new_email: user.email || "", password: "" });
      api.get("/customer/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
      loadLoyalty();
      loadNewsletter();
    }
  }, [user]);

  if (user === null) {
    return (
      <div data-testid="account-loading" className="flex items-center justify-center gap-3 py-32 text-bark-soft">
        <Loader2 className="animate-spin text-amber-deep" size={28} />
        <span className="font-serif text-xl">Chargement de votre espace…</span>
      </div>
    );
  }
  if (user === false) return <Navigate to="/compte/connexion" replace />;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/customer/profile", profile);
      await refreshUser();
      toast.success("Coordonnées enregistrées");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      const { data } = await api.put("/customer/email", emailForm);
      toast.success(data.message);
      setEmailForm({ new_email: data.email, password: "" });
      await refreshUser();
      loadNewsletter();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingEmail(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm) {
      toast.error("Les deux nouveaux mots de passe ne correspondent pas");
      return;
    }
    setSavingPassword(true);
    try {
      const { data } = await api.put("/customer/password", {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success(data.message);
      setPasswordForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingPassword(false);
    }
  };

  const handleNlSave = async () => {    setSavingNl(true);
    try {
      const { data } = await api.put("/customer/newsletter", { frequency: nlFrequency });
      toast.success(data.message);
      setNewsletter({ subscribed: true, frequency: data.frequency });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingNl(false);
    }
  };

  const handleNlUnsubscribe = async () => {
    if (!window.confirm("Vous désinscrire de la Gazette Champêtre ?")) return;
    setSavingNl(true);
    try {
      const { data } = await api.post("/customer/newsletter/unsubscribe");
      toast.success(data.message);
      setNewsletter((cur) => ({ ...(cur || {}), subscribed: false }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingNl(false);
    }
  };

  const handleCancelBooking = async (sessionId, slotId) => {
    if (!window.confirm("Annuler cette réservation ? Les places seront remises en vente et le remboursement déclenché automatiquement.")) return;
    try {
      const { data } = await api.post("/customer/bookings/cancel", { session_id: sessionId, slot_id: slotId });
      toast.success(data.message || "Réservation annulée");
      const { data: refreshed } = await api.get("/customer/orders");
      setOrders(refreshed);
      loadLoyalty();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const points = loyalty?.points ?? user.loyalty_points ?? 0;

  return (
    <div data-testid="customer-account-page" className="mx-auto max-w-4xl px-4 py-12 sm:px-8">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow">Mon compte</p>
          <h1 className="mt-2 font-serif text-4xl font-semibold tracking-tight text-bark">
            Bonjour {user.name || "ami·e des abeilles"}
          </h1>
          <p data-testid="account-email" className="mt-1 text-sm text-stone-mute">{user.email}</p>
          <SchoolOrderLink />
        </div>
        <button
          data-testid="customer-logout"
          onClick={handleLogout}
          className="flex items-center gap-2 rounded-full border border-terra/30 px-5 py-2.5 text-sm font-medium text-terra transition-colors hover:bg-terra/10"
        >
          <LogOut size={15} /> Me déconnecter
        </button>
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        <button
          data-testid="tab-coordonnees"
          onClick={() => setTab("coordonnees")}
          className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${tab === "coordonnees" ? "bg-amber-deep text-white shadow-md shadow-amber-900/15" : "border border-amber-800/20 bg-white text-bark-soft hover:border-amber-deep hover:text-amber-deep"}`}
        >
          <MapPin size={15} /> Mes coordonnées
        </button>
        <button
          data-testid="tab-commandes"
          onClick={() => setTab("commandes")}
          className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${tab === "commandes" ? "bg-amber-deep text-white shadow-md shadow-amber-900/15" : "border border-amber-800/20 bg-white text-bark-soft hover:border-amber-deep hover:text-amber-deep"}`}
        >
          <Package size={15} /> Mes commandes
        </button>
        <button
          data-testid="tab-fidelite"
          onClick={() => setTab("fidelite")}
          className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${tab === "fidelite" ? "bg-amber-deep text-white shadow-md shadow-amber-900/15" : "border border-amber-800/20 bg-white text-bark-soft hover:border-amber-deep hover:text-amber-deep"}`}
        >
          <Star size={15} /> Ma fidélité
        </button>
      </div>

      {tab === "coordonnees" && (
        <div className="space-y-6">
          <MyChildren />
          <form data-testid="profile-form" onSubmit={handleSave} className="space-y-5 rounded-2xl border border-amber-800/15 bg-white p-8 shadow-sm">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="pr-name" className="mb-1.5 block text-sm font-medium text-bark">Nom</label>
                <input id="pr-name" data-testid="pr-name" required value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="pr-phone" className="mb-1.5 block text-sm font-medium text-bark">Téléphone</label>
                <input id="pr-phone" data-testid="pr-phone" type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className={inputClass} placeholder="06 12 34 56 78" />
              </div>
            </div>
            <div>
              <label htmlFor="pr-address" className="mb-1.5 block text-sm font-medium text-bark">Adresse</label>
              <input id="pr-address" data-testid="pr-address" value={profile.address_line} onChange={(e) => setProfile({ ...profile, address_line: e.target.value })} className={inputClass} placeholder="12 rue des Oliviers" />
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="pr-postal" className="mb-1.5 block text-sm font-medium text-bark">Code postal</label>
                <input id="pr-postal" data-testid="pr-postal" value={profile.postal_code} onChange={(e) => setProfile({ ...profile, postal_code: e.target.value })} className={inputClass} placeholder="04210" />
              </div>
              <div>
                <label htmlFor="pr-city" className="mb-1.5 block text-sm font-medium text-bark">Ville</label>
                <input id="pr-city" data-testid="pr-city" value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} className={inputClass} placeholder="Grenoble" />
              </div>
            </div>
            <p className="text-xs text-stone-mute">Ces coordonnées pré-rempliront vos prochaines commandes et nous aident à préparer vos envois.</p>
            <button
              data-testid="profile-save"
              type="submit"
              disabled={saving}
              className="rounded-full bg-amber-deep px-7 py-3 text-sm font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:opacity-60"
            >
              {saving ? "Enregistrement…" : "Enregistrer mes coordonnées"}
            </button>
          </form>

          <form data-testid="email-form" onSubmit={handleEmailChange} className="space-y-5 rounded-2xl border border-amber-800/15 bg-white p-8 shadow-sm">
            <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-bark">
              <AtSign size={18} className="text-honey" /> Adresse e-mail de connexion
            </h2>
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="em-new" className="mb-1.5 block text-sm font-medium text-bark">Nouvelle adresse e-mail</label>
                <input id="em-new" data-testid="em-new" type="email" required value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="em-password" className="mb-1.5 block text-sm font-medium text-bark">Mot de passe actuel *</label>
                <PasswordInput id="em-password" data-testid="em-password" required value={emailForm.password} onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })} className={inputClass} placeholder="Pour confirmer le changement" />
              </div>
            </div>
            <p className="text-xs text-stone-mute">Votre inscription à la Gazette et votre historique de commandes suivront automatiquement votre nouvelle adresse.</p>
            <button
              data-testid="email-save"
              type="submit"
              disabled={savingEmail}
              className="rounded-full bg-amber-deep px-7 py-3 text-sm font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:opacity-60"
            >
              {savingEmail ? "Mise à jour…" : "Changer mon adresse e-mail"}
            </button>
          </form>

          <form data-testid="password-form" onSubmit={handlePasswordChange} className="space-y-5 rounded-2xl border border-amber-800/15 bg-white p-8 shadow-sm">
            <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-bark">
              <KeyRound size={18} className="text-honey" /> Mot de passe
            </h2>
            <div className="grid gap-5 sm:grid-cols-3">
              <div>
                <label htmlFor="pw-current" className="mb-1.5 block text-sm font-medium text-bark">Mot de passe actuel *</label>
                <PasswordInput id="pw-current" data-testid="pw-current" required value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="pw-new" className="mb-1.5 block text-sm font-medium text-bark">Nouveau mot de passe *</label>
                <PasswordInput id="pw-new" data-testid="pw-new" required minLength={8} value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} className={inputClass} placeholder="8 caractères minimum" />
              </div>
              <div>
                <label htmlFor="pw-confirm" className="mb-1.5 block text-sm font-medium text-bark">Confirmation *</label>
                <PasswordInput id="pw-confirm" data-testid="pw-confirm" required minLength={8} value={passwordForm.confirm} onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })} className={inputClass} placeholder="Retapez-le" />
              </div>
            </div>
            <button
              data-testid="password-save"
              type="submit"
              disabled={savingPassword}
              className="rounded-full bg-amber-deep px-7 py-3 text-sm font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:opacity-60"
            >
              {savingPassword ? "Mise à jour…" : "Changer mon mot de passe"}
            </button>
          </form>

          <div data-testid="newsletter-card" className="space-y-5 rounded-2xl border border-amber-800/15 bg-white p-8 shadow-sm">
            <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-bark">
              <Mail size={18} className="text-honey" /> Gazette Champêtre
            </h2>
            {!newsletter ? (
              <p className="text-sm text-bark-soft">Chargement…</p>
            ) : (
              <>
                <p data-testid="newsletter-status" className="text-sm text-bark-soft">
                  {newsletter.subscribed
                    ? <>Vous êtes inscrit·e — réception <strong className="text-amber-deep">{FREQUENCY_OPTIONS.find((f) => f.value === newsletter.frequency)?.label || "1 fois par mois"}</strong>.</>
                    : "Vous n'êtes pas inscrit·e — choisissez une périodicité pour recevoir nouvelles de la ruche, recettes et récoltes en avant-première."}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-stone-mute">Recevoir la Gazette :</span>
                  {FREQUENCY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`account-newsletter-frequency-${opt.value}`}
                      onClick={() => setNlFrequency(opt.value)}
                      className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all duration-200 ${
                        nlFrequency === opt.value
                          ? "bg-amber-deep text-white shadow-sm"
                          : "border border-amber-900/20 text-bark-soft hover:border-amber-deep hover:text-amber-deep"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    data-testid="newsletter-save"
                    type="button"
                    onClick={handleNlSave}
                    disabled={savingNl}
                    className="rounded-full bg-amber-deep px-7 py-3 text-sm font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:opacity-60"
                  >
                    {savingNl ? "Enregistrement…" : newsletter.subscribed ? "Mettre à jour ma périodicité" : "M'abonner à la Gazette"}
                  </button>
                  {newsletter.subscribed && (
                    <button
                      data-testid="newsletter-unsubscribe"
                      type="button"
                      onClick={handleNlUnsubscribe}
                      disabled={savingNl}
                      className="rounded-full border border-terra/30 px-5 py-3 text-sm font-medium text-terra transition-colors hover:bg-terra/10 disabled:opacity-60"
                    >
                      Me désinscrire
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "fidelite" && (
        <div data-testid="customer-loyalty" className="space-y-6">
          <ReferralShare />
          <ChildThanks />
          <div className="rounded-2xl border border-amber-800/15 bg-white p-8 shadow-sm">
            <div className="flex items-center gap-4">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-amber-50">
                <Star size={26} className="fill-honey text-honey" />
              </span>
              <div>
                <p data-testid="loyalty-balance" className="font-serif text-3xl font-semibold text-bark">{points} point{points > 1 ? "s" : ""}</p>
                <p data-testid="loyalty-scheme" className="text-sm text-bark-soft">
                  {loyaltySettings
                    ? `${loyaltySettings.points_per_euro} point${loyaltySettings.points_per_euro > 1 ? "s" : ""} gagné${loyaltySettings.points_per_euro > 1 ? "s" : ""} par euro dépensé · 1 point = ${formatPrice(loyaltySettings.point_value_cents)} de remise`
                    : "Chargement du barème…"}
                </p>
              </div>
            </div>
            <p className="mt-4 text-xs text-stone-mute">
              Vos points se dépensent d'un simple geste : cochez « Utiliser mes points » dans le panier avant de commander.
            </p>
          </div>
          <div className="rounded-2xl border border-amber-800/15 bg-white p-8 shadow-sm">
            <h2 className="mb-4 font-serif text-xl font-semibold text-bark">Historique de vos points</h2>
            {!loyalty ? (
              <p className="text-sm text-bark-soft">Chargement…</p>
            ) : loyalty.movements.length === 0 ? (
              <p data-testid="loyalty-empty" className="text-sm text-bark-soft">Pas encore de mouvement — vos prochains achats vous rapporteront des points.</p>
            ) : (
              <ul className="divide-y divide-amber-900/10">
                {loyalty.movements.map((m) => (
                  <li key={m.session_id} data-testid={`loyalty-movement-${m.session_id}`} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                    <div>
                      <p className="font-medium text-bark">{m.label || `Commande n° ${m.session_id.slice(-8).toUpperCase()}`}</p>
                      <p className="text-xs text-stone-mute">
                        {m.created_at ? new Date(m.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : ""}{m.amount != null ? ` · ${formatPrice(Math.round(m.amount * 100))}` : ""}
                      </p>
                    </div>
                    <div className="flex gap-2 text-xs font-semibold">
                      {m.points_earned > 0 && <span data-testid={`loyalty-earned-${m.session_id}`} className="rounded-full bg-sage-light px-3 py-1 text-sage">+{m.points_earned} gagnés</span>}
                      {m.points_used > 0 && <span data-testid={`loyalty-used-${m.session_id}`} className="rounded-full bg-terra/15 px-3 py-1 text-terra">−{m.points_used} utilisés</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {tab === "commandes" && (
        <div data-testid="customer-orders" className="space-y-4">
          {!orders ? (
            <p className="py-16 text-center font-serif text-xl text-bark-soft">Chargement…</p>
          ) : orders.length === 0 ? (
            <div className="rounded-2xl border border-amber-800/15 bg-white p-12 text-center shadow-sm">
              <Receipt size={36} className="mx-auto text-amber-900/20" />
              <p data-testid="orders-empty" className="mt-4 font-serif text-xl text-bark-soft">Pas encore de commande — la ruche vous attend !</p>
            </div>
          ) : (
            orders.map((o) => (
              <div key={o.session_id} data-testid={`customer-order-${o.session_id}`} className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-stone-mute">
                      {o.created_at ? new Date(o.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : ""} · n° {o.session_id.slice(-8).toUpperCase()}
                    </p>
                    <p className="mt-1 font-serif text-xl font-semibold text-bark">{formatPrice(Math.round((o.amount || 0) * 100))}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {o.gift_wrap && (
                      <span className="flex items-center gap-1.5 rounded-full bg-terra/10 px-3 py-1 text-xs font-medium text-terra">
                        <Gift size={12} /> Emballage cadeau
                      </span>
                    )}
                    {o.payment_status === "paid" && (o.points_earned > 0 || o.points_used > 0) && (
                      <span data-testid={`order-points-${o.session_id}`} className="flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-deep">
                        <Star size={12} className="fill-honey text-honey" />
                        {o.points_earned > 0 ? `+${o.points_earned} pts` : ""}
                        {o.points_earned > 0 && o.points_used > 0 ? " · " : ""}
                        {o.points_used > 0 ? `−${o.points_used} pts` : ""}
                      </span>
                    )}
                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${PAYMENT_STYLES[o.payment_status] || "bg-sand text-bark-soft"}`}>
                      {PAYMENT_LABELS[o.payment_status] || o.payment_status}
                    </span>
                    {o.reassigned_to_school ? (
                      <span data-testid={`order-reassigned-${o.session_id}`} className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">Remise à l'école {o.reassigned_to_school.school_name}</span>
                    ) : o.payment_status === "paid" && !["expediee", "livree"].includes(o.fulfillment_status) && (
                      <button data-testid={`order-reassign-${o.session_id}`} onClick={() => openReassign(o)} className="rounded-full border border-amber-800/25 px-3 py-1 text-xs font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep">Réassigner à une école</button>
                    )}
                    {o.payment_status === "paid" && !o.reassigned_to_school && (
                      <span data-testid={`order-fulfillment-label-${o.session_id}`} className="rounded-full bg-sage-light px-3 py-1 text-xs font-medium text-sage">
                        {FULFILLMENT_LABELS[o.fulfillment_status || "preparation"]}
                      </span>
                    )}
                  </div>
                </div>
                {["pending", "awaiting_cheque"].includes(o.payment_status) && (
                  <PendingOrderActions order={o} onChanged={() => api.get("/customer/orders").then(({ data }) => setOrders(data)).catch(() => {})} />
                )}
                {o.delivery?.slot_id && o.payment_status === "paid" && (
                  <div data-testid={`order-delivery-slot-${o.session_id}`} className={`mt-3 flex items-start gap-2 rounded-xl px-4 py-3 text-sm ${o.delivery.status === "cancelled" ? "bg-terra/10 text-terra" : o.delivery.status === "delivered" ? "bg-sage-light text-sage" : "bg-amber-50 text-bark"}`}>
                    <CalendarClock size={16} className="mt-0.5 shrink-0 text-amber-deep" />
                    {o.delivery.status === "cancelled" ? (
                      <span>Créneau du {o.delivery.label} annulé — nous revenons vers vous pour convenir d'une nouvelle date.</span>
                    ) : o.delivery.status === "delivered" ? (
                      <span>Livrée — créneau du {o.delivery.label}</span>
                    ) : (
                      <span>Livraison prévue : <strong>{o.delivery.label}</strong></span>
                    )}
                  </div>
                )}
                {o.items?.length > 0 && (
                  <ul className="mt-4 space-y-1 border-t border-amber-900/10 pt-4 text-sm text-bark-soft">
                    {o.items.map((item) => (
                      <li key={item.slot_id || item.product_id} className="flex flex-wrap items-center justify-between gap-2">
                        <span>
                          {item.quantity} × {item.name}
                          {item.discount_percent > 0 && (
                            <span data-testid={`order-item-discount-${item.slot_id || item.product_id}`} className="ml-2 rounded-full bg-sage-light px-2.5 py-0.5 text-xs font-medium text-sage">
                              −{item.discount_percent} % volume
                            </span>
                          )}
                          {item.slot_label && <span className="block text-xs font-medium text-amber-deep">Créneau : {item.slot_label}</span>}
                          {item.cancelled && (
                            <span className="mt-0.5 inline-block rounded-full bg-terra/15 px-2.5 py-0.5 text-xs font-medium text-terra">
                              Annulée{item.refunded ? " · remboursée" : ""}
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-3">
                          <span className="font-medium text-bark">{formatPrice(item.unit_amount_cents * item.quantity)}</span>
                          {item.slot_id && !item.cancelled && o.payment_status === "paid" && (item.slot_label || "").split(" · ")[0] > new Date().toISOString().slice(0, 10) && (
                            <button
                              data-testid={`cancel-booking-${item.slot_id}`}
                              onClick={() => handleCancelBooking(o.session_id, item.slot_id)}
                              className="rounded-full border border-terra/30 px-3 py-1 text-xs font-medium text-terra transition-colors hover:bg-terra/10"
                            >
                              Annuler
                            </button>
                          )}
                        </span>
                      </li>
                    ))}
                    {o.loyalty_discount_cents > 0 && (
                      <li data-testid={`order-loyalty-discount-${o.session_id}`} className="flex justify-between gap-4 text-sage">
                        <span>Remise points fidélité</span>
                        <span>−{formatPrice(o.loyalty_discount_cents)}</span>
                      </li>
                    )}
                    {o.shipping && (
                      <li className="flex justify-between gap-4 text-stone-mute">
                        <span>Livraison — {o.shipping.name}</span>
                        <span>{o.shipping.charge_cents > 0 ? formatPrice(o.shipping.charge_cents) : "Offerte"}</span>
                      </li>
                    )}
                  </ul>
                )}
              </div>
            ))
          )}
        </div>
      )}
      {reassignOrder && (
        <ReassignSchoolModal schools={schoolChoices.schools} currentSchoolId={schoolChoices.current_school_id} askCode onSubmit={submitReassign} onClose={() => setReassignOrder(null)} />
      )}
    </div>
  );
}
